import { useTranslation } from 'react-i18next'

import { Checkbox, DatePicker, Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import {
  useClientContacts,
  useClients,
} from '@/features/directories/api/useDirectory'
import {
  draftFromRecord,
  EMPTY_QUOTE_DRAFT,
  type QuoteDraft,
} from '@/features/quotes/quoteDraft'
import { isSuppressed, type DirectoryContact, type QuoteDetail } from '@/types/domain'

/* "New quote" — the entry to UJ-1.
 *
 * This sheet takes the HEADER only: client, validity, and the terms that are
 * agreed at the same moment. Cargo and cost lines are entered on the quote
 * itself, where the checklist can watch them and the totals can recalculate
 * live. A modal that tried to hold all three would be the Excel path with extra
 * steps, and quoting must not be slower than the Excel it replaces.
 *
 * The client is a DIRECTORY PICK rather than a typed name (FR-39). That is the
 * substitution the whole product exists to make: the source workbook joined its
 * sheets on a company name somebody retyped each time, and the same client
 * arrived as four spellings. Typing a name here would have rebuilt that.
 */

/* The draft shape, the validators and the empty header come from quoteDraft.ts,
 * which was extracted for exactly this reason: this sheet and step 1 of the
 * create form ask the same questions, and two copies of these predicates would
 * eventually disagree about whether "20 " is a number — one surface sending
 * what the other refused.
 *
 * The copies that used to live in this file were byte-identical to that module
 * and outlived the extraction. They are gone now: the participants list (FR-24)
 * was the first field to exist in one and not the other, and a second shape
 * would have made toQuotePayload silently drop the parties submitted here. */

/** A whole number, or blank. Empty is legitimate: transit time is optional. */
function isBlankOrCount(raw: string): boolean {
  const normalised = raw.trim()
  return normalised === '' || /^\d+$/.test(normalised)
}

/** A decimal measure, or blank. A comma is accepted because that is what a
 *  Russian keyboard produces, and the payload normalises it to a dot. */
function isBlankOrDecimal(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised === '' || /^\d+(\.\d+)?$/.test(normalised)
}

export function QuoteFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  /** Absent for a new quote.
   *
   *  QuoteDetail rather than QuoteSummary, because the EDIT prefill needs
   *  `cargoTotals` — the list shape carries a rounder `cargo` summary instead,
   *  and prefilling the three measure overrides from a summary is not possible.
   *  Nothing is lost by narrowing it: the only surface that edits a header is
   *  the detail page, which holds the detail. */
  record?: QuoteDetail
  onSubmit: (values: QuoteDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  // Searchable, so a directory of hundreds stays usable. The list is the one
  // this caller may see — Sales sees their own clients, and the server is what
  // decides that (AD-5).
  const { data: clients } = useClients()

  const [draft, setDraft, touched, markTouched] = useFormDraft<QuoteDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? draftFromRecord(record)
        : { ...emptyQuote, quoteDate: today() },
  )

  const clientError =
    touched && !draft.clientId ? t('common.form.required') : undefined

  /* The selected client's own contacts (FR-4, FR-39), from the DETAIL read.
     Not from the client list this sheet already holds: that list carries no
     contacts at all — directory.ListClients loads none, by the same "a
     directory of two hundred clients does not need six hundred contact rows"
     argument the service states — so reading them from it left this picker
     permanently empty however many contacts the client had.

     The hook preserves the directory's ordering, which is load-bearing:
     quote.firstAddressable picks "the first one carrying an email, primary
     first", so a re-sort would make the hint below name a different person from
     the one the send would actually address. */
  const contacts = useClientContacts(draft.clientId || undefined)

  /* Who the offer goes to when no contact is named: the first carrying an
     address. The same predicate the server applies, stated once here so the hint
     cannot claim one person and the send reach another.

     `email` is a suppressible field, so a contact whose address this caller may
     not read is not offered as the default — it would name somebody the reader
     cannot verify. isSuppressed is the check, never a truthiness test: a
     Suppressed value is an OBJECT and would pass one. */
  const addressable = (contact: DirectoryContact): boolean =>
    !isSuppressed(contact.email) && (contact.email ?? '').trim() !== ''
  const defaultContact = contacts.find(addressable)

  /* Every contact is offered, not only the addressable ones. A named contact is
     a deliberate choice the server does not second-guess "even when that contact
     carries no address" (quote.withDefaultRecipient), so hiding the addressless
     ones would remove a choice the domain allows — and the send refuses loudly
     rather than silently mailing somebody else. */
  const contactOptions = contacts.map((contact) => ({
    value: contact.id,
    label: contact.label,
  }))

  const transitInvalid =
    !isBlankOrCount(draft.transitDaysMin) || !isBlankOrCount(draft.transitDaysMax)
  // A minimum above its maximum is not a range. Caught here as well as on the
  // server so the correction happens while the two fields are still on screen.
  const transitInverted =
    draft.transitDaysMin !== '' &&
    draft.transitDaysMax !== '' &&
    Number(draft.transitDaysMin) > Number(draft.transitDaysMax)

  const transitError =
    touched && transitInvalid
      ? t('common.form.invalidNumber')
      : touched && transitInverted
        ? t('quotes.form.transitInverted')
        : undefined

  /* The header measures. Blank is legitimate on all three — it means the sum
     stands — so only a non-numeric entry is refused, which is a typo. */
  const measureInvalid = (raw: string): boolean => !isBlankOrDecimal(raw)
  const grossWeightError =
    touched && measureInvalid(draft.grossWeightKgOverride)
      ? t('common.form.invalidNumber')
      : undefined
  const volumeError =
    touched && measureInvalid(draft.volumeCbmOverride)
      ? t('common.form.invalidNumber')
      : undefined
  const chargeableWeightError =
    touched && measureInvalid(draft.chargeableWeightKg)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    !draft.clientId ||
    transitInvalid ||
    transitInverted ||
    measureInvalid(draft.grossWeightKgOverride) ||
    measureInvalid(draft.volumeCbmOverride) ||
    measureInvalid(draft.chargeableWeightKg)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('quotes.actions.editQuote') : t('quotes.actions.newQuote')}
      submitLabel={record ? undefined : t('quotes.actions.create')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('quotes.columns.client')} required error={clientError}>
        {(props) => (
          <Select
            value={draft.clientId}
            /* The contact is cleared with the client. A contact id belonging to
               the previous client would name a person at a different company —
               and the server would refuse the send rather than notice, so the
               failure would arrive late and read as a directory problem. */
            onChange={(clientId) =>
              setDraft({ ...draft, clientId, clientContactId: '' })
            }
            options={(clients ?? []).map((client) => ({
              value: client.id,
              label: client.name,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      {/* FR-4's "customer AND contact" — the person at the client this offer is
          addressed to.
          
          A pick from the selected client's own contacts, never free text: the
          address the offer is emailed to has to be one the directory holds, and
          quote.resolveRecipients refuses any other (a crafted request would
          otherwise email the company's own quoted prices anywhere).
          
          Reset when the client changes, above: a contact belonging to the
          previous client would name a person at a different company.
          
          Blank is the ordinary case and is not a gap. With no contact named the
          server addresses the offer to the client's first contact carrying an
          email, and the detail read shows that same one — one rule, so the
          address SHOWN and the address SENT TO cannot drift. The hint says which
          it will be rather than leaving the reader to guess. */}
      <Field
        label={t('quotes.form.clientContact')}
        hint={
          draft.clientContactId === '' && defaultContact
            ? t('quotes.form.clientContactDefault', { name: defaultContact.label })
            : t('common.form.optional')
        }
      >
        {(props) => (
          <Select
            value={draft.clientContactId}
            onChange={(clientContactId) => setDraft({ ...draft, clientContactId })}
            options={contactOptions}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      {/* FR-4's header cargo — whose goods these are and what they are, in one
          line each. NOT the FR-12 positions, which carry their own descriptions
          and are entered on the cargo tab: this is what prints above that table,
          and `cargoOwner` is routinely not the party paying the freight, which
          is why it is its own field rather than the client's name. */}
      <Field label={t('quotes.form.cargoOwner')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.cargoOwner}
            onChange={(e) => setDraft({ ...draft, cargoOwner: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('quotes.form.cargoDescription')}
        hint={t('common.form.optional')}
      >
        {(props) => (
          <Input
            value={draft.cargoDescription}
            onChange={(e) =>
              setDraft({ ...draft, cargoDescription: e.target.value })
            }
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.form.pickupLocation')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.pickupLocation}
            onChange={(e) => setDraft({ ...draft, pickupLocation: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The two dates sit side by side: they are one thought — how long this
          offer stands — and separating them reads as two questions. */}
      <div className="flex gap-2.5">
        <Field label={t('quotes.form.quoteDate')} required className="flex-1">
          {(props) => (
            <DatePicker
              value={draft.quoteDate}
              onChange={(quoteDate) => setDraft({ ...draft, quoteDate })}
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.columns.validUntil')}
          hint={t('quotes.form.validUntilHint')}
          className="flex-1"
        >
          {(props) => (
            <DatePicker
              value={draft.validUntil}
              onChange={(validUntil) => setDraft({ ...draft, validUntil })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* FR-23. Two numbers, not "20-25 days": a text range cannot be compared
          against an actual transit time and cannot survive conversion into the
          case without somebody re-typing it. */}
      <Field
        label={t('quotes.form.transitDays')}
        hint={t('common.form.optional')}
        error={transitError}
      >
        {(props) => (
          <div className="flex items-center gap-2">
            <Input
              inputMode="numeric"
              value={draft.transitDaysMin}
              onChange={(e) => setDraft({ ...draft, transitDaysMin: e.target.value })}
              aria-label={t('quotes.form.transitDaysMin')}
              className="asa-num"
              {...props}
            />
            <span className="text-text-faint shrink-0" aria-hidden>
              –
            </span>
            <Input
              inputMode="numeric"
              value={draft.transitDaysMax}
              onChange={(e) => setDraft({ ...draft, transitDaysMax: e.target.value })}
              aria-label={t('quotes.form.transitDaysMax')}
              className="asa-num"
            />
          </div>
        )}
      </Field>

      <Field label={t('quotes.form.incoterm')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.incoterm}
            onChange={(e) =>
              setDraft({ ...draft, incoterm: e.target.value.toUpperCase() })
            }
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.form.paymentTerms')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.paymentTerms}
            onChange={(e) => setDraft({ ...draft, paymentTerms: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* FR-11's deal terms, in their own fieldset because they are one subject:
          what a delay costs. Both are DISPLAYED on the quote's terms tab and,
          until now, could be filled by nothing — the screen showed "Demurrage —"
          for a value the product had no way to enter.

          `deliveryTerms` is deliberately in here and NOT beside the Incoterm
          above, so the two are not read as one field: the Incoterm is the ICC
          rule that assigns risk and cost (FR-59), and this is our own sentence
          about how delivery actually happens. The hint says so. */}
      <fieldset className="flex flex-col gap-2.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.form.dealTermsLegend')}
        </legend>

        <Field
          label={t('quotes.form.demurrageTerms')}
          hint={t('quotes.form.demurrageTermsHint')}
        >
          {(props) => (
            <Input
              value={draft.demurrageTerms}
              onChange={(e) =>
                setDraft({ ...draft, demurrageTerms: e.target.value })
              }
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.form.deliveryTerms')}
          hint={t('quotes.form.deliveryTermsHint')}
        >
          {(props) => (
            <Input
              value={draft.deliveryTerms}
              onChange={(e) => setDraft({ ...draft, deliveryTerms: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </fieldset>

      {/* The FR-13 header measures. Blank is the normal state and means the
          sum over the positions stands — the hints say so, because a figure
          typed here silently outranks the arithmetic on the offer. */}
      <fieldset className="flex flex-col gap-2.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.form.measures')}
        </legend>

        <div className="flex gap-2.5">
          <Field
            label={t('quotes.form.grossWeightOverride')}
            error={grossWeightError}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.grossWeightKgOverride}
                onChange={(e) =>
                  setDraft({ ...draft, grossWeightKgOverride: e.target.value })
                }
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field
            label={t('quotes.form.volumeOverride')}
            error={volumeError}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.volumeCbmOverride}
                onChange={(e) =>
                  setDraft({ ...draft, volumeCbmOverride: e.target.value })
                }
                className="asa-num"
                {...props}
              />
            )}
          </Field>
        </div>

        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('quotes.form.overrideHint')}
        </p>

        <Field
          label={t('quotes.cargo.chargeableWeight')}
          error={chargeableWeightError}
          hint={t('quotes.form.chargeableWeightHint')}
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.chargeableWeightKg}
              onChange={(e) =>
                setDraft({ ...draft, chargeableWeightKg: e.target.value })
              }
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Checkbox
          label={t('quotes.form.finalPackingList')}
          checked={draft.finalPackingListReceived}
          onCheckedChange={(checked) =>
            setDraft({ ...draft, finalPackingListReceived: checked })
          }
        />
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('quotes.form.finalPackingListHint')}
        </p>
      </fieldset>

      {/* FR-4's optional carrier/supplier DISPLAY line.
          
          LAST, and in its own fieldset with the warning attached, because the
          second clause of Story 2.1's criterion is the load-bearing one: this is
          informational only and assigns NO vendor to any cost line. 0006's column
          comment says the same and
          TestAnonTheCarrierLineIsTextAndNotAParticipantReference pins that it
          never resolves to a participant id.
          
          Free text, therefore, and deliberately not a picker over participants:
          a dropdown of counterparties here would look exactly like the vendor
          field it must not be, and the first person to use it that way would be
          recording an agent on a quote in a column no suppression rule covers
          (FR-29, AD-6). Placed away from the costing fields for the same reason
          — proximity would imply a relationship that does not exist. */}
      <fieldset className="flex flex-col gap-2.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.form.carrierDisplayLegend')}
        </legend>

        <Field
          label={t('quotes.form.carrierDisplay')}
          hint={t('quotes.form.carrierDisplayHint')}
        >
          {(props) => (
            <Input
              value={draft.carrierDisplay}
              onChange={(e) =>
                setDraft({ ...draft, carrierDisplay: e.target.value })
              }
              {...props}
            />
          )}
        </Field>
      </fieldset>

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('quotes.form.nextStep')}
      </p>
    </FormSheet>
  )
}

/** Today, date-only (AD-19). A quote defaults to the day it is written. */
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/* The shared blank, aliased so the call sites below read as they always did.
 * This sheet does not collect participants and no create surface does any more:
 * a quote does not name its external parties on the way in. They are added
 * afterwards, on the detail page's own section, or on the case. */
const emptyQuote: QuoteDraft = EMPTY_QUOTE_DRAFT
